import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PromoCountdown from "@/components/PromoCountdown";
import { isPromoActive, PROMO_DISCOUNT_LABEL, PROMO_DATE_LABEL } from "@/config/promo";
import CookieBanner from "@/components/CookieBanner";
import {
  Package,
  Zap,
  Sparkles,
  Crown,
  Check,
  ArrowRight,
  Clock,
  Users,
  BarChart3,
  Shield,
  Database,
  TrendingUp,
  ChevronDown
} from "lucide-react";

// Custom hook for scroll reveal animation
function useScrollReveal() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isVisible];
}

// Animated section component
function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useScrollReveal();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(40px)",
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
}

// Floating mockup component
function FloatingMockup({ src, alt, className = "", floatClass = "animate-float" }) {
  return (
    <div className={`relative ${className}`}>
      {/* Device frame */}
      <div className={`relative rounded-2xl overflow-hidden shadow-2xl ${floatClass}`} style={{ boxShadow: '0 30px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.1)' }}>
        {/* Browser chrome */}
        <div className="bg-slate-900 px-4 py-3 flex items-center gap-2 border-b border-slate-700/50">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
            <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
          </div>
          <div className="flex-1 mx-4">
            <div className="bg-slate-700 rounded-md px-3 py-1 text-xs text-slate-600 text-center tracking-wide">
              skiflowrental.com
            </div>
          </div>
        </div>
        {/* Screenshot */}
        <img
          src={src}
          alt={alt}
          className="w-full h-auto block"
          loading="lazy"
        />
      </div>
      {/* Ambient glow */}
      <div className="absolute -inset-6 bg-gradient-to-br from-blue-600/15 via-purple-600/10 to-pink-600/15 rounded-3xl blur-3xl -z-10"></div>
    </div>
  );
}

// Glass card component
function GlassCard({ children, className = "" }) {
  return (
    <div className={`relative group ${className}`}>
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl border border-white/20 shadow-xl"></div>
      <div className="relative p-8">{children}</div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "¿Por qué el periodo de prueba es ilimitado?",
      answer: "Queremos que pongas a prueba a SkiFlow Rental al máximo nivel. Durante los 14 días de Trial, no tendrás límites de artículos, clientes ni usuarios. Al finalizar, te asesoraremos para elegir el plan que mejor se adapte al volumen real de datos que has gestionado."
    },
    {
      question: "¿Qué ocurre si necesito subir de plan a mitad de temporada?",
      answer: "Puedes hacer un upgrade de forma instantánea. Solo se te cobrará la diferencia proporcional entre tu plan actual y el nuevo, para que tu inversión sea siempre justa y eficiente."
    },
    {
      question: "¿Qué pasa si me quedo sin internet en la tienda?",
      answer: "SkiFlow Rental incluye un Modo Offline en todos sus planes. Puedes seguir emitiendo tickets y gestionando alquileres sin interrupciones; en cuanto el sistema detecte conexión, los datos se sincronizarán automáticamente."
    },
    {
      question: "¿Realmente funciona con mi hardware actual?",
      answer: "Sí. Nuestro sistema es \"Plug & Play\". Conecta tus lectores de códigos e impresoras térmicas USB y empieza a trabajar sin necesidad de instalar drivers complejos."
    },
    {
      question: "¿El soporte técnico tiene coste extra?",
      answer: "No. En SkiFlow Rental el soporte está incluido en todos los planes anuales. Tu tranquilidad y el correcto funcionamiento de tu tienda son nuestra prioridad."
    },
    {
      question: "¿Mis datos están seguros?",
      answer: "Totalmente. Realizamos copias de seguridad diarias en la nube y utilizamos bases de datos optimizadas para ofrecerte una respuesta en milisegundos, sin importar cuántos miles de clientes tengas."
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float 7s ease-in-out infinite;
          animation-delay: 1.5s;
        }
        .animate-float-slow {
          animation: float 8s ease-in-out infinite;
          animation-delay: 1s;
        }
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient-shift 8s ease infinite;
        }
        .glass-card {
          background: rgba(255,255,255,0.07);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.15);
          transition: background 0.3s, border-color 0.3s, transform 0.3s;
        }
        .glass-card:hover {
          background: rgba(255,255,255,0.12);
          border-color: rgba(255,255,255,0.3);
          transform: translateY(-4px);
        }
      `}</style>

      {/* Sticky Navbar - Con Degradado y Glassmorphism */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl border-b border-white/10" style={{ background: 'linear-gradient(135deg, rgba(30,58,138,0.95) 0%, rgba(124,58,237,0.95) 50%, rgba(236,72,153,0.95) 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img src="/logo-white.png" alt="SkiFlow Rental - Software TPV para alquiler de esquís" className="h-10 w-auto object-contain" />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <button 
                onClick={() => navigate("/login")}
                className="text-white/90 hover:text-white font-semibold text-base transition-colors"
              >
                Iniciar Sesión
              </button>
              <Button 
                onClick={() => navigate("/register")}
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-bold px-8 py-3 rounded-full transition-all border border-white/20 shadow-lg hover:shadow-xl"
              >
                Crear Cuenta Gratis
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Con Mockup Flotante */}
      <section className="min-h-[95vh] flex items-center relative overflow-hidden animate-gradient" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #ec4899 100%)' }}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <div>
              <AnimatedSection>
                <div className="inline-flex items-center mb-5">
                  <span className="text-xs font-black text-white uppercase tracking-widest px-5 py-2 rounded-full" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.25)' }}>
                    El Nº1 en SOFTWARE EN ALQUILER EQUIPOS NIEVE PARA TIENDAS FÍSICAS
                  </span>
                </div>
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-8 leading-[0.95] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  Despacha a tus clientes en <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-white via-pink-200 to-white">segundos</span>, no en minutos.
                </h1>
              </AnimatedSection>
              
              <AnimatedSection delay={100}>
                <p className="text-xl sm:text-2xl text-white/90 mb-12 max-w-xl font-medium leading-relaxed">
                  El software de alquiler exclusivo para tiendas de esquí y snowboard. Elimina las colas en el mostrador, despacha material más rápido y controla tu stock físico en tiempo real de manera eficiente. El TPV diseñado para dominar al máximo la temporada alta.
                </p>
              </AnimatedSection>
              
              {/* Botones CTA */}
              <AnimatedSection delay={200}>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button 
                    size="lg"
                    className="text-base px-10 py-7 bg-white text-black hover:bg-white/90 font-bold rounded-full shadow-2xl hover:shadow-white/30 transition-all hover:scale-105"
                    onClick={() => navigate("/register")}
                  >
                    Empezar Prueba Gratis
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline"
                    className="text-base px-10 py-7 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white/20 font-bold rounded-full transition-all"
                    onClick={() => navigate("/login")}
                  >
                    Ver Demo en Vivo
                  </Button>
                </div>
              </AnimatedSection>
              
              <AnimatedSection delay={300}>
                <div className="flex flex-wrap items-center gap-6 text-sm text-white/80">
                  <span className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-white" />
                    Sin tarjeta de crédito
                  </span>
                  <span className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-white" />
                    14 días gratis
                  </span>
                  <span className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-white" />
                    Cancela cuando quieras
                  </span>
                </div>
              </AnimatedSection>
            </div>

            {/* Right: Floating Mockup */}
            <AnimatedSection delay={400} className="hidden lg:block">
              <FloatingMockup 
                src="/mockup-inventario.png" 
                alt="Vista del inventario en el TPV de alquiler de esquís de SkiFlow Rental"
                className="transform rotate-1 hover:rotate-0 transition-transform duration-700"
                floatClass="animate-float"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-32 overflow-hidden bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <div className="mb-20">
              <h2 className="text-5xl sm:text-6xl font-black text-black mb-6 leading-tight tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                El motor de tu rentabilidad
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl font-medium">
                Menos clics, más alquileres. Diseñado para el ritmo real de la montaña.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <AnimatedSection delay={0}>
              <div className="group p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-300 h-full">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Database className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">
                  Gestión de Inventario en Tiempo Real
                </h3>
                <p className="text-slate-600 text-base leading-relaxed">
                  Controla cada par de esquís y cada bota con códigos internos. Visualiza estados de disponibilidad al instante para que nunca prometas material que no tienes en el taller.
                </p>
              </div>
            </AnimatedSection>

            {/* Feature 2 */}
            <AnimatedSection delay={80}>
              <div className="group p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-300 h-full">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Users className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">
                  Base de Datos de Clientes 360°
                </h3>
                <p className="text-slate-600 text-base leading-relaxed">
                  Acceso total al historial de alquileres y créditos pendientes. Recupera los datos de años anteriores en milisegundos para agilizar la ficha técnica y el ajuste de fijaciones.
                </p>
              </div>
            </AnimatedSection>

            {/* Feature 3 */}
            <AnimatedSection delay={160}>
              <div className="group p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-300 h-full">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-pink-600 to-red-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Zap className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">
                  Packs Inteligentes de Alta Velocidad
                </h3>
                <p className="text-slate-600 text-base leading-relaxed">
                  Configura packs de "Material + Casco + Botas". El sistema los detecta automáticamente al escanear, aplicando la tarifa correcta sin que tu staff tenga que calcular nada a mano.
                </p>
              </div>
            </AnimatedSection>

            {/* Feature 4 */}
            <AnimatedSection delay={240}>
              <div className="group p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-300 h-full">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Clock className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">
                  Proceso de Salida "Turbo"
                </h3>
                <p className="text-slate-600 text-base leading-relaxed">
                  Crea alquileres en segundos combinando el uso de códigos de barras, packs automáticos y precios dinámicos según la temporada. Cierra el ticket y pasa al siguiente cliente antes de que se forme cola.
                </p>
              </div>
            </AnimatedSection>

            {/* Feature 5 */}
            <AnimatedSection delay={320}>
              <div className="group p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-300 h-full">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <BarChart3 className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">
                  Informes y Análisis de Rentabilidad
                </h3>
                <p className="text-slate-600 text-base leading-relaxed">
                  Identifica qué ítems son los más alquilados y cuáles se quedan en el estante. Estadísticas completas para tomar decisiones basadas en datos reales, no en intuiciones.
                </p>
              </div>
            </AnimatedSection>

            {/* Feature 6 */}
            <AnimatedSection delay={400}>
              <div className="group p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-300 h-full">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Shield className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">
                  Acceso Multiusuario por Tienda
                </h3>
                <p className="text-slate-600 text-base leading-relaxed">
                  Gestiona tu local desde varios ordenadores simultáneamente. Crea perfiles de empleado independientes para que cada miembro del equipo tenga su propio acceso bajo una misma licencia.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* App Showcase Section */}
      <section className="py-32 relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-20">
            <h2 className="text-4xl sm:text-5xl font-black text-black mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Mira cómo funciona
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Una interfaz intuitiva diseñada para la velocidad
            </p>
          </AnimatedSection>

          {/* Row 1: Nuevo Alquiler + Steps */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-28">
            <AnimatedSection>
              <FloatingMockup 
                src="/mockup-alquiler.png" 
                alt="Pantalla de nuevo alquiler en el software TPV para tiendas de nieve SkiFlow"
                floatClass="animate-float-delayed"
              />
            </AnimatedSection>
            
            <AnimatedSection delay={200}>
              <div className="space-y-8">
                <div className="flex items-start gap-5">
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0 text-white font-black text-lg shadow-lg">1</div>
                  <div>
                    <h3 className="text-xl font-bold text-black mb-2">Escanea el código de barras</h3>
                    <p className="text-slate-600">Compatible con cualquier lector USB. Plug & Play.</p>
                  </div>
                </div>
                <div className="flex items-start gap-5">
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center flex-shrink-0 text-white font-black text-lg shadow-lg">2</div>
                  <div>
                    <h3 className="text-xl font-bold text-black mb-2">Selecciona el cliente</h3>
                    <p className="text-slate-600">Búsqueda instantánea por nombre o DNI.</p>
                  </div>
                </div>
                <div className="flex items-start gap-5">
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-pink-600 to-red-500 flex items-center justify-center flex-shrink-0 text-white font-black text-lg shadow-lg">3</div>
                  <div>
                    <h3 className="text-xl font-bold text-black mb-2">Cobra y entrega</h3>
                    <p className="text-slate-600">Efectivo, tarjeta o mixto. Ticket impreso al instante.</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* Row 2: Dashboard + Description (reversed) */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection delay={100} className="order-2 lg:order-1">
              <div className="space-y-8">
                <div className="flex items-start gap-5">
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0 text-white shadow-lg">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-black mb-2">Panel de control en tiempo real</h3>
                    <p className="text-slate-600">Alquileres activos, devoluciones pendientes y ocupación de stock a golpe de vista.</p>
                  </div>
                </div>
                <div className="flex items-start gap-5">
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 text-white shadow-lg">
                    <BarChart3 className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-black mb-2">Vista semanal de ocupación</h3>
                    <p className="text-slate-600">Anticipa la demanda día a día para gestionar tu stock con antelación.</p>
                  </div>
                </div>
                <div className="flex items-start gap-5">
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0 text-white shadow-lg">
                    <Zap className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-black mb-2">Acceso multiusuario simultáneo</h3>
                    <p className="text-slate-600">Todos los miembros del equipo conectados al mismo tiempo desde distintos ordenadores.</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200} className="order-1 lg:order-2">
              <FloatingMockup 
                src="/mockup-dashboard.png" 
                alt="Panel de control del software de gestión de alquiler de material deportivo de nieve"
                floatClass="animate-float-slow"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats Section - Con Glassmorphism */}
      <section className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #ec4899 100%)' }}>
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-3 gap-8">
            <AnimatedSection>
              <div className="text-center p-8 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20">
                <div className="text-6xl font-black text-white mb-3">3X</div>
                <div className="text-lg text-white/80 font-medium">Más rápido que sistemas tradicionales</div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={100}>
              <div className="text-center p-8 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20">
                <div className="text-6xl font-black text-white mb-3">100%</div>
                <div className="text-lg text-white/80 font-medium">Control de tu inventario en tiempo real</div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div className="text-center p-8 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20">
                <div className="text-6xl font-black text-white mb-3">0</div>
                <div className="text-lg text-white/80 font-medium">Colas en temporada alta</div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <AnimatedSection>
          <div className="mb-20 text-center">
            <h2 className="text-5xl sm:text-6xl font-black text-black mb-6 leading-tight tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Planes que escalan con tu inventario
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto font-medium">
              Sin letras pequeñas. Todos los planes incluyen TODAS las funciones.
            </p>
          </div>
        </AnimatedSection>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Trial Plan */}
          <AnimatedSection delay={0}>
            <div className="relative group h-full">
              <div className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 blur transition duration-500" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #ec4899 100%)' }}></div>
              <div className="relative bg-white rounded-2xl border-2 border-slate-200 p-8 hover:border-transparent transition-all duration-300 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #ec4899 100%)' }}>
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-black">Trial</h3>
                    <p className="text-xs text-slate-500">Prueba gratuita</p>
                  </div>
                </div>
                
                <div className="mb-8">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-black">0 €</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-2 font-medium">14 días gratis</p>
                </div>

                <Button 
                  onClick={() => navigate("/register")} 
                  variant="outline" 
                  className="w-full mb-8 font-bold border-2 border-black text-black hover:bg-black hover:text-white rounded-full py-6 transition-all hover:scale-105"
                >
                  PROBAR GRATIS
                </Button>

                <div className="space-y-4 flex-1">
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Artículos ilimitados</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Clientes ilimitados</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Usuarios ilimitados</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Todas las funciones</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Soporte incluido</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Modo Offline</span>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Basic Plan */}
          <AnimatedSection delay={100}>
            <div className="relative group h-full">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-slate-700 to-slate-900 rounded-2xl opacity-0 group-hover:opacity-100 blur transition duration-500"></div>
              <div className="relative bg-white rounded-2xl border-2 border-slate-200 p-8 hover:border-transparent transition-all duration-300 h-full flex flex-col">
                {isPromoActive() && (
                  <div className="absolute -top-3 right-4 flex flex-col items-end gap-1">
                    <Badge className="bg-red-500 text-white font-black px-3 py-1 shadow-lg text-xs">
                      {PROMO_DISCOUNT_LABEL} hasta {PROMO_DATE_LABEL}
                    </Badge>
                    <PromoCountdown className="text-[10px] font-bold text-red-600 bg-white/90 rounded px-1.5 py-0.5 shadow-sm" data-testid="basic-promo-countdown" />
                  </div>
                )}
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-12 w-12 rounded-xl bg-black flex items-center justify-center shadow-lg">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-black">Basic</h3>
                    <p className="text-xs text-slate-500">Para empezar</p>
                  </div>
                </div>
                
                <div className="mb-8">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-black">950 €</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1 font-medium">+ IVA (21%) = <strong>1.149,50 €</strong>/año</p>
                </div>

                <Button 
                  onClick={() => navigate("/register?plan=basic")} 
                  variant="outline" 
                  className="w-full mb-8 font-bold border-2 border-black text-black hover:bg-black hover:text-white rounded-full py-6 transition-all hover:scale-105"
                  data-testid="basic-plan-cta"
                >
                  CONTRATAR
                </Button>

                <div className="space-y-4 flex-1">
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Hasta 1.000 artículos</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Hasta 5.000 clientes</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Hasta 5 usuarios</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Todas las funciones</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Soporte incluido</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Modo Offline</span>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Pro Plan - DESTACADO */}
          <AnimatedSection delay={200}>
            <div className="relative group lg:scale-105 h-full">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl opacity-100 blur-sm animate-gradient" style={{ backgroundSize: '200% 200%' }}></div>
              <div className="relative bg-black rounded-2xl p-8 shadow-2xl h-full flex flex-col">
                <div className="absolute -top-4 left-4">
                  <Badge className="bg-white text-black font-black px-4 py-1.5 shadow-lg text-xs uppercase tracking-wider">
                    Popular
                  </Badge>
                </div>
                {isPromoActive() && (
                  <div className="absolute -top-3 right-4 flex flex-col items-end gap-1">
                    <Badge className="bg-red-500 text-white font-black px-3 py-1 shadow-lg text-xs" data-testid="pro-discount-badge">
                      {PROMO_DISCOUNT_LABEL} hasta {PROMO_DATE_LABEL}
                    </Badge>
                    <PromoCountdown className="text-[10px] font-bold text-red-500 bg-white/90 rounded px-1.5 py-0.5 shadow-sm" data-testid="pro-promo-countdown" />
                  </div>
                )}
                
                <div className="flex items-center gap-3 mb-6 mt-2">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-xl">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Pro</h3>
                    <p className="text-xs text-slate-600">Más vendido</p>
                  </div>
                </div>
                
                <div className="mb-8">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-white">1.450 €</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1 font-medium">+ IVA (21%) = <strong className="text-white">1.754,50 €</strong>/año</p>
                </div>

                <Button 
                  onClick={() => navigate("/register?plan=pro")} 
                  className="w-full mb-8 bg-white text-black hover:bg-slate-100 font-black rounded-full py-6 shadow-lg hover:shadow-xl transition-all hover:scale-105"
                  data-testid="pro-plan-cta"
                >
                  CONTRATAR AHORA
                </Button>

                <div className="space-y-4 flex-1">
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-white font-semibold">Hasta 3.000 artículos</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-white font-semibold">Hasta 40.000 clientes</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-white font-semibold">Hasta 10 usuarios</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-white font-semibold">Todas las funciones</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-white font-semibold">Soporte incluido</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-white font-semibold">Modo Offline</span>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Enterprise Plan */}
          <AnimatedSection delay={300}>
            <div className="relative group h-full">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-slate-700 to-slate-900 rounded-2xl opacity-0 group-hover:opacity-100 blur transition duration-500"></div>
              <div className="relative bg-white rounded-2xl border-2 border-slate-200 p-8 hover:border-transparent transition-all duration-300 h-full flex flex-col">
                {isPromoActive() && (
                  <div className="absolute -top-3 right-4 flex flex-col items-end gap-1">
                    <Badge className="bg-red-500 text-white font-black px-3 py-1 shadow-lg text-xs" data-testid="enterprise-discount-badge">
                      {PROMO_DISCOUNT_LABEL} hasta {PROMO_DATE_LABEL}
                    </Badge>
                    <PromoCountdown className="text-[10px] font-bold text-red-600 bg-white/90 rounded px-1.5 py-0.5 shadow-sm" data-testid="enterprise-promo-countdown" />
                  </div>
                )}
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-12 w-12 rounded-xl bg-black flex items-center justify-center shadow-lg">
                    <Crown className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-black">Enterprise</h3>
                    <p className="text-xs text-slate-500">Sin límites</p>
                  </div>
                </div>
                
                <div className="mb-8">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-black">1.950 €</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1 font-medium">+ IVA (21%) = <strong>2.359,50 €</strong>/año</p>
                </div>

                <Button 
                  onClick={() => navigate("/register?plan=enterprise")} 
                  variant="outline" 
                  className="w-full mb-8 font-bold border-2 border-black text-black hover:bg-black hover:text-white rounded-full py-6 transition-all hover:scale-105"
                  data-testid="enterprise-plan-cta"
                >
                  CONTRATAR
                </Button>

                <div className="space-y-4 flex-1">
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Artículos ilimitados</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Clientes ilimitados</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Hasta 15 usuarios</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Todas las funciones</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Soporte incluido</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium">Modo Offline</span>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <AnimatedSection>
          <div className="text-center mb-16">
            <h2 className="text-5xl sm:text-6xl font-black text-black mb-6 leading-tight tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Preguntas Frecuentes
            </h2>
            <p className="text-xl text-slate-600 font-medium">
              Todo lo que necesitas saber sobre SkiFlow Rental
            </p>
          </div>
        </AnimatedSection>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <AnimatedSection key={index} delay={index * 50}>
              <div 
                className="bg-white border-2 border-slate-200 rounded-2xl overflow-hidden transition-all duration-300 hover:border-slate-300 hover:shadow-lg"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left transition-colors hover:bg-slate-50"
                >
                  <span className="text-lg font-bold text-black pr-8">
                    {faq.question}
                  </span>
                  <ChevronDown 
                    className={`h-6 w-6 text-black flex-shrink-0 transition-transform duration-300 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                <div 
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-8 pb-6 text-slate-600 text-base leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #ec4899 100%)' }}>
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <AnimatedSection>
            <h2 className="text-5xl sm:text-6xl font-black text-white mb-6 leading-tight tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              ¿Listo para eliminar las colas de una vez por todas?
            </h2>
            <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto font-medium leading-relaxed">
              Únete a los profesionales que ya han recuperado el "Flow" de su negocio con SkiFlow Rental.
            </p>
            <Button 
              size="lg"
              className="bg-white text-black hover:bg-white/90 text-base px-12 py-7 font-bold rounded-full shadow-2xl hover:shadow-white/20 transition-all hover:scale-105"
              onClick={() => navigate("/register")}
            >
              EMPEZAR PRUEBA ILIMITADA GRATIS
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer - MINIMAL */}
      <footer className="bg-white border-t border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Logo */}
            <div className="col-span-2">
              <img src="/logo.png" alt="SkiFlow Rental - Software TPV para alquiler de equipos de esquí y snowboard" className="h-10 w-auto object-contain mb-6" />
              <p className="text-slate-600 max-w-md leading-relaxed">
                Software de gestión de alquiler de equipos de esquí. Rápido, intuitivo y diseñado para la temporada alta.
              </p>
            </div>

            {/* Producto */}
            <div>
              <h3 className="font-bold mb-4 text-black">Producto</h3>
              <ul className="space-y-3 text-slate-600">
                <li><button onClick={() => navigate("/register")} className="hover:text-black transition-colors">Funcionalidades</button></li>
                <li><button onClick={() => navigate("/landing#pricing")} className="hover:text-black transition-colors">Precios</button></li>
                <li><button onClick={() => navigate("/login")} className="hover:text-black transition-colors">Demo</button></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="font-bold mb-4 text-black">Legal</h3>
              <ul className="space-y-3 text-slate-600">
                <li><a href="/politica-privacidad" className="hover:text-black transition-colors">Política de Privacidad</a></li>
                <li><a href="/politica-cookies" className="hover:text-black transition-colors">Política de Cookies</a></li>
                <li><a href="mailto:hola@skiflowrental.com" className="hover:text-black transition-colors">Contacto</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-slate-500 text-sm">
            <p>&copy; 2026 SkiFlow Rental · FASCINADS SOLUCIONES SL · B70880182. Todos los derechos reservados.</p>
            <div className="flex gap-4">
              <a href="/politica-privacidad" className="hover:text-slate-700 transition-colors">Privacidad</a>
              <a href="/politica-cookies" className="hover:text-slate-700 transition-colors">Cookies</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      <CookieBanner />
    </div>
  );
}
